"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, useEffect } from "react";
import styles from "./recognition.module.scss";

const recognitionItems = [
  {
    src: "/logos/national-challenge-fund.png",
    alt: "National Challenge Fund Logo",
    title: "🏆 Winner – National Challenge Fund: Future Digital Challenge",
    description:
      "AI2Peat was awarded a total of €1.75M through the National Challenge Fund’s Future Digital Challenge, recognising our work in developing AI-powered remote sensing tools to monitor, map, and restore Ireland’s peatlands at scale.",
    link: "https://www.sfi.ie/challenges/National-Challenge-Fund/",
  },
  {
    src: "/logos/ESALogo.jpg",
    alt: "ESA Logo",
    title: "Finalist – ESA LPS 2025 Sustainable Horizons Award",
    description:
      "AI2Peat has been shortlisted as one of only three global finalists for the ESA Living Planet Symposium’s Sustainable Horizons Award 2025, recognising our innovative contributions to sustainability in earth observation.",
    link: "https://lps25.esa.int/lps-sustainable-horizons-award/",
  },
  {
    src: "/logos/AIireland.jpg",
    alt: "AI Awards 2025 Logo",
    title: "Finalist – AI Awards 2025: Best Application of AI in Sustainability",
    description:
      "AI2Peat has been honoured as the winner of the 2025 AI Awards in the category ‘Best Application of AI in Sustainability’, recognising our impactful use of artificial intelligence to advance environmental sustainability and climate innovation.",
    link: "https://aiawards.ie/",
  },
  {
    src: "/logos/award-logo.png",
    alt: "UCD Impact Award Logo",
    title: "🎓 Runner-up – UCD Impact Case Award",
    description:
      "AI2Peat was recognised as a runner-up in the UCD Impact Case Awards, highlighting the real-world societal and environmental impact of our peatland condition monitoring platform developed in partnership with University College Dublin.",
    link: "https://www.ucd.ie/",
  },
];


export default function Recognition() {
  const [current, setCurrent] = useState(0);
  const total = recognitionItems.length;

  useEffect(() => {
    const id = setInterval(() => {
      setCurrent((prev) => (prev + 1) % total);
    }, 5000);
    return () => clearInterval(id);
  }, [total]);

  const handleNext = () => setCurrent((prev) => (prev + 1) % total);
  const handlePrev = () => setCurrent((prev) => (prev - 1 + total) % total);

  const item = recognitionItems[current];

  return (
    <section className={styles.recognition} aria-labelledby="partners-heading">
      {/* background gradient and orbs */}
      <div className={styles.backgroundElements}>
        <div className={styles.blueOrb1}></div>
        <div className={styles.blueOrb2}></div>
        <div className={styles.blueOrb3}></div>
      </div>

      <h3 id="partners-heading" className={styles.title}>
        🏆 Recognition
      </h3>

      <div className={styles.cardWrapper}>
        {/* arrows */}
        <button
          type="button"
          className={`${styles.navBtn} ${styles.leftBtn}`}
          onClick={handlePrev}
          aria-label="Previous award"
        >
          ‹
        </button>
        <button
          type="button"
          className={`${styles.navBtn} ${styles.rightBtn}`}
          onClick={handleNext}
          aria-label="Next award"
        >
          ›
        </button>

        <div className={styles.container}>
          {/* image slider */}
          <div className={styles.imageWrapper} aria-hidden="true">
            {recognitionItems.map((logo, index) => (
              <div
                key={logo.title}
                className={`${styles.imageContainer} ${
                  index === current ? styles.active : ""
                }`}
              >
                {logo.src ? (
                  <Image
                    src={logo.src}
                    alt={logo.alt}
                    fill
                    sizes="(max-width: 767px) 120px, 180px"
                    className={styles.image}
                    priority={index === 0}
                  />
                ) : (
                  <div className={styles.placeholder}>
                    <span>🏆</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* text */}
          <div className={styles.text}>
            <h2>{item.title}</h2>
            <p>{item.description}</p>
            {item.link && (
              <Link
                href={item.link}
                target="_blank"
                className={styles.link}
                rel="noreferrer"
              >
                Learn more →
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* indicator dots (not clickable) */}
      <div className={styles.dots} aria-label="Award indicator">
        {recognitionItems.map((_, index) => (
          <span
            key={index}
            className={`${styles.dot} ${
              index === current ? styles.activeDot : ""
            }`}
            aria-current={index === current ? "true" : "false"}
          />
        ))}
      </div>
    </section>
  );
}
